import clsx from 'clsx';
import * as React from 'react';

import { Icon } from '@gitbook/spine-icons';
import { AtomButton, type ButtonProps, Tooltip } from '@gitbook/spine-react';

export type DropLinePosition = 'top' | 'bottom' | 'left' | 'right';
export type DropLineButtonAlign = 'start' | 'middle' | 'end';

/**
 * Line to be highlighted during drag and drop.
 * The line should be positionned in a relative elements.
 *
 * It can show a small button.
 */
export const DropLine = React.forwardRef(
    (
        props: {
            /** If true, the line is always highlighted */
            highlighted?: boolean;
            /** If true, the line is considered as being hovered for dropping an item */
            dropHovered: boolean;
            /** Position of the line relative to its parent. */
            position: DropLinePosition;
            /** If true, the button is visibile independently from the user hovering the line*/
            buttonVisible?: boolean;
            /** Alignment of the button in the line */
            buttonAlign?: DropLineButtonAlign;
            /** Button to integrate in the line. */
            button?: ButtonProps;
            /** Offset on each side of the line */
            offsetStart?: number;
            offsetEnd?: number;
            insertButton?: {
                onPress: () => void;
                label: string;
            };
        },
        ref: React.Ref<HTMLDivElement>
    ) => {
        const {
            highlighted,
            dropHovered,
            position,
            buttonVisible,
            offsetStart = 0,
            offsetEnd = 0,
            insertButton,
        } = props;

        const direction = position === 'top' || position === 'bottom' ? 'horizontal' : 'vertical';

        return (
            <div
                ref={ref}
                className="group/line absolute z-10"
                style={{
                    ...positionStyles[position].lineWrapper,
                    ...(direction === 'horizontal'
                        ? {
                              left: offsetStart,
                              right: offsetEnd,
                          }
                        : {
                              top: offsetStart,
                              bottom: offsetEnd,
                          }),
                }}
            >
                {insertButton ? (
                    <Tooltip label={insertButton.label}>
                        <AtomButton
                            onPress={insertButton.onPress}
                            className={clsx(
                                'peer absolute z-[1] inline-flex rounded-full p-1',
                                highlighted
                                    ? 'bg-action-primary text-white'
                                    : 'bg-base text-muted hover:bg-action-primary hover:text-white',
                                !dropHovered && (buttonVisible || highlighted)
                                    ? 'opacity-100'
                                    : 'opacity-0 group-hover/line:opacity-100'
                            )}
                            style={positionStyles[position].button}
                        >
                            <Icon.Plus size={10} />
                        </AtomButton>
                    </Tooltip>
                ) : null}
                <div
                    className={clsx(
                        'absolute',
                        direction === 'horizontal'
                            ? '-translate-y-1/2 top-1/2 right-0 left-0 h-0.5'
                            : '-translate-x-1/2 top-0 bottom-0 left-1/2 h-auto w-0.5',
                        dropHovered || highlighted
                            ? 'bg-action-primary'
                            : 'peer-hover:bg-action-primary'
                    )}
                />
            </div>
        );
    }
);

const LINE_WIDTH = 2;
const LINE_WRAPPER_WIDTH = 12;
const BUTTON_OFFSET_MAIN_AXIS = -LINE_WRAPPER_WIDTH / 3;
const BUTTON_OFFSET_CROSS_AXIS = -(LINE_WRAPPER_WIDTH - LINE_WRAPPER_WIDTH / 3);

const positionStyles = {
    top: {
        lineWrapper: {
            left: 0,
            right: 0,
            top: -8,
            height: LINE_WRAPPER_WIDTH,
        },
        button: {
            left: BUTTON_OFFSET_CROSS_AXIS,
            top: BUTTON_OFFSET_MAIN_AXIS,
        },
    },
    bottom: {
        lineWrapper: {
            bottom: -LINE_WRAPPER_WIDTH / 2,
            left: 0,
            right: 0,
            height: LINE_WRAPPER_WIDTH,
        },
        line: {
            bottom: -(LINE_WRAPPER_WIDTH - LINE_WIDTH) / 2,
            left: 0,
            right: 0,
            height: LINE_WIDTH,
        },
        button: {
            left: BUTTON_OFFSET_CROSS_AXIS,
            top: BUTTON_OFFSET_MAIN_AXIS,
        },
    },
    left: {
        lineWrapper: {
            top: 0,
            bottom: 0,
            left: -LINE_WRAPPER_WIDTH / 2,
            width: LINE_WRAPPER_WIDTH,
            display: 'flex',
            justifyContent: 'center',
        },
        line: {
            top: 0,
            bottom: 0,
            left: (LINE_WRAPPER_WIDTH - LINE_WIDTH) / 2,
            width: LINE_WIDTH,
        },
        button: {
            top: BUTTON_OFFSET_CROSS_AXIS,
        },
    },
    right: {
        lineWrapper: {
            top: 0,
            bottom: 0,
            right: -LINE_WRAPPER_WIDTH / 2,
            width: LINE_WRAPPER_WIDTH,
            display: 'flex',
            justifyContent: 'center',
        },
        line: {
            top: 0,
            bottom: 0,
            right: (LINE_WRAPPER_WIDTH - LINE_WIDTH) / 2,
            width: LINE_WIDTH,
        },
        button: {
            top: BUTTON_OFFSET_CROSS_AXIS,
        },
    },
};
